/**
 * COMPONENT: BedWizard
 * Bulletin d'Engagement Définitif — parcours participant en 6 étapes.
 *
 * Monté par `PublicFormPage` UNIQUEMENT pour un formulaire `generates_bed`
 * ouvert avec un `?token=` : les formulaires BPI classiques gardent
 * strictement leur rendu d'origine.
 *
 * Ce que le composant garantit (cahier des charges + brief vague 1) :
 *   - 6 étapes, frise cliquable, saut direct vers une étape déjà visitée ;
 *   - AUTO-SAVE SERVEUR à chaque changement d'étape et à intervalle régulier :
 *     le participant ferme, revient par le même lien, tout est là ; si tout est
 *     complet et non payé, il atterrit directement sur ⑥ (cahier L94) ;
 *   - échappatoire « je m'engage à communiquer avant le … » sur les
 *     informations critiques, avec mise en avant en en-tête à chaque retour ;
 *   - solde SERVEUR, options branchées sur le socle 00166 ;
 *   - événements `DESERTOURS_BED_STATE` vers le parent à chaque transition,
 *     sans remplacer `DESERTOURS_PAYMENT_READY` (rétro-compatibilité).
 */

import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CloudUpload,
  Loader2,
  Pencil,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  notifyParentBedPaymentReady,
  notifyParentBedState,
  notifyParentScrollToTop,
  type BedMissingItemPayload,
} from '../../lib/analytics';
import {
  finalizeBedSubmission,
  getBedBootstrap,
  purgeBedCertificate,
  getBedEventRules,
  getBedVehicleHint,
  resumeBedPayment,
  saveBedDraft,
  type BedBootstrap,
  type BedEventRules,
  type BedCertificate,
  type BedDraftPatch,
  type BedPricingItem,
  type BedResumePaymentState,
  type BedSignature,
  type BedTokenError,
} from '../../lib/bed/draft';
import { buildBedPaymentLink } from '../../lib/bed/admin';
import { buildBedPrefillValues } from '../../lib/bed/prefill';
import {
  BED_LAST_STEP,
  BED_STEPS,
  buildFieldIndex,
  buildResumeCtaHint,
  buildResumeCtaLabel,
  buildRuleContext,
  canLeaveStep,
  clampAmountToPay,
  computeMissingItems,
  computeStepState,
  computeTotalCents,
  deferralKey,
  deferralsToKeepOnResubmit,
  describeStepBlocker,
  dropCoveredCertificateDeferrals,
  excludeResolvedMissingItems,
  formatCents,
  groupFieldsByStep,
  isFieldRequired,
  isGroupDeferred,
  isConditionsAcknowledged,
  isFieldVisible,
  isChecked,
  isPassportExpiryField,
  isResubmittableStatus,
  normalizeRegistration,
  passportExpiryError,
  landingStep,
  pickEffectiveMissingItems,
  promiseRemainderCents,
  roleKeyOf,
  participantFullName,
  hiddenValuesToClear,
  STUDENT_TRUE_VALUE,
  type BedDeferralGroup,
  type BedDeferralMap,
  buildPostSubmitResumeState,
  reusableSubmissionId,
  type BedStepId,
  type BedStepState,
  type BedValues,
  type RoleSubmissionCache,
} from '../../lib/bed/wizard-model';
import { useFixedRootFontSize } from '../../lib/bed/use-fixed-root-font-size';
import { formatDate } from '../../lib/date-utils';
import {
  createBedPaymentCase,
  type BedPaymentCaseData,
} from '../../lib/payment/create-bed-payment-case';
import { publicSupabase } from '../../lib/supabase';
import type { Form, FormFieldWithValues } from '../../types/form';
import { Button } from '../ui';
import { BedCeamHint } from './BedCeamHint';
import { BedDeferrableGroup } from './BedDeferrableGroup';
import {
  BED_DEROGATION_FIELD_NOTICE,
  BED_DEROGATION_FINAL_PENDING_MESSAGE,
  BED_DEROGATION_HOLD_MESSAGE,
  BED_DEROGATION_HOLD_TITLE,
  BED_DEROGATION_PENDING_MESSAGE,
  productHasAgeLimit,
} from '../../lib/bed/derogation-messages';
import { isOver28AtEventDate } from '../../lib/age-utils';
import { BedFieldInput } from './BedFieldInput';
import { BedMissingBanner } from './BedMissingBanner';
import { BedOptionsStep } from './BedOptionsStep';
import { BedRecapStep, type BedParticipantRole, type BedRecapAmounts } from './BedRecapStep';
import { BedSchoolField } from './BedSchoolField';
import { BedTimeline } from './BedTimeline';

interface BedWizardProps {
  form: Form;
  fields: FormFieldWithValues[];
  token: string;
  /** Mode iframe : le message de fin s'affiche en ligne, sans toast. */
  isEmbedded: boolean;
}

/** Intervalle de sauvegarde automatique en arrière-plan (ms). */
const AUTOSAVE_INTERVAL_MS = 20_000;

/** Ordre des groupes reportables dans une étape participant. */
const DEFERRABLE_ORDER: BedDeferralGroup[] = ['passport', 'permis', 'ceam', 'vehicule'];
// 00252 (retour 1) : champs d'identité verrouillés quand le BPI les a fournis.
const LOCKED_CONTACT_MAPS = ['first_name', 'last_name', 'birth_date'];
// 00252 (retour 17) : order_index absolus de l'étape véhicule.
const OI_VEHICLE_PLATE = 4010;
const OI_VEHICLE_DATE = 4020;
// 00252 (retour 33) : code moteur, facultatif — la phrase d'aide vient de l'équipe 4L.
const OI_VEHICLE_ENGINE_CODE = 4030;
const ENGINE_CODE_HINT =
  'La plaque se trouve sur le côté gauche du bloc moteur (quand vous êtes face au capot). Cherchez un peu en dessous et à l’arrière de l’allumeur (tête de delco) et du filtre à huile.';
const OWNER_AUTHORIZATION_PDF = '/documents/autorisation-vehicule-tiers.pdf';
const OWNER_AUTHORIZATION_DOCX = '/documents/autorisation-vehicule-tiers.docx';
/** 00280 : option ajoutée au premier envoi sur un dossier dont le paiement est engagé. */
const OPTION_LOCKED_FIRST_SUBMISSION_MESSAGE =
  'Votre paiement est déjà engagé : une option ne peut plus être ajoutée en ligne. Décochez-la pour enregistrer votre bulletin, ou écrivez-nous à service.administratif@desertours.com.';
/** 00280 : un paiement par carte est en cours, le montant du dossier ne peut pas bouger. */
const PAYMENT_IN_PROGRESS_MESSAGE =
  'Un paiement par carte est en cours sur votre dossier : réessayez dans quelques minutes. Vos réponses sont conservées.';
/** 00296 : rien à payer, une option payante ne peut pas être prise sans dossier de paiement. */
const OPTIONS_REQUIRE_PAYMENT_MESSAGE =
  'Votre solde est déjà couvert : une option payante ne peut pas être ajoutée en ligne. Décochez-la pour enregistrer votre bulletin, ou écrivez-nous à service.administratif@desertours.com.';
/** 00296 : la finalisation sans dossier a été refusée ou a échoué : jamais un succès. */
const NOTHING_DUE_FINALIZE_FAILED_MESSAGE =
  'Votre bulletin n’a pas pu être finalisé. Vos réponses sont conservées : réessayez dans un instant, ou écrivez-nous à service.administratif@desertours.com.';

type WizardPhase = 'loading' | 'ready' | 'submitting' | 'submitted';

type LoadError = 'invalid' | 'expired' | 'error';

interface SubmitOutcome {
  complete: boolean;
  paymentCaseId: string | null;
  missingCount: number;
  amountToPayCents: number | null;
  degraded: 'no_parent' | 'failed' | null;
  /** 00296 : finalisé sans dossier de paiement, rien à payer. */
  nothingDue: boolean;
  /**
   * 00312 (TICKET-DEROG-04) : le paiement du solde attend la validation d'une
   * dérogation d'âge. Le message remplace le paiement ; jamais de `payment_ready`.
   */
  derogationPending: boolean;
  /**
   * 00312 : rien à payer et dérogation manquante — le bulletin n'a PAS été
   * envoyé (brouillon seulement). Écran dédié, ni montant ni réédition.
   */
  notSubmitted?: boolean;
}

export function BedWizard({ form, fields, token, isEmbedded }: BedWizardProps) {
  // Le CRM dimensionne sa racine en `vw` (maquette 1920 px) : sur un téléphone,
  // tout deviendrait illisible. Le bulletin se dimensionne comme une page web
  // normale, et rend la main en sortant.
  useFixedRootFontSize();

  const [phase, setPhase] = useState<WizardPhase>('loading');
  const [loadError, setLoadError] = useState<LoadError | null>(null);
  const [bootstrap, setBootstrap] = useState<BedBootstrap | null>(null);

  const [values, setValues] = useState<BedValues>({});
  const [deferred, setDeferred] = useState<BedDeferralMap>({});
  const [signatures, setSignatures] = useState<Partial<Record<BedParticipantRole, BedSignature>>>(
    {}
  );
  const [certificates, setCertificates] = useState<
    Partial<Record<BedParticipantRole, BedCertificate>>
  >({});
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState<Record<string, string>>({});
  const [doubleRoomAnswer, setDoubleRoomAnswer] = useState<'oui' | 'non' | null>(null);
  const [amountToPayCents, setAmountToPayCents] = useState<number | null>(null);

  const [currentStep, setCurrentStep] = useState<BedStepId>(1);
  const [showErrors, setShowErrors] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  /** Éléments manquants faisant foi : ceux que le SERVEUR a recalculés. */
  const [serverMissing, setServerMissing] = useState<BedMissingItemPayload[] | null>(null);
  /**
   * Le bulletin a déjà été ouvert au moins une fois (brouillon serveur existant
   * ou première étape enregistrée). Conditionne l'en-tête : le cahier L16 parle
   * d'une mise en avant « quand il RETOURNE sur le BED » — sur un bulletin
   * vierge, lister d'emblée tout ce qui reste à saisir ne met rien en avant.
   */
  const [isReturning, setIsReturning] = useState(false);
  const [outcome, setOutcome] = useState<SubmitOutcome | null>(null);
  /**
   * Réédition d'un bulletin déjà soumis (migration 00248, retour recette du
   * 04/09/2026) : le serveur autorise tant que l'équipage n'est ni « Inscrit »
   * ni « BED complet ». `isResubmitting` : le participant a rouvert le
   * formulaire depuis l'écran « bulletin enregistré ».
   */
  const [resubmitAllowed, setResubmitAllowed] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  /** Relecture du bootstrap au clic sur « Compléter / Modifier mon bulletin ». */
  const [resubmitBusy, setResubmitBusy] = useState(false);
  const [resubmitError, setResubmitError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * Reprise du paiement (migration 00237) — review Victor, P0 « cul-de-sac
   * avant paiement ». Renseigné par le bootstrap dès que le bulletin est soumis
   * et que le solde reste dû : c'est ce qui décide d'afficher le CTA au
   * RECHARGEMENT, là où le `postMessage` n'a jamais lieu.
   */
  const [resumeState, setResumeState] = useState<BedResumePaymentState | null>(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  /**
   * Soumissions déjà passées, par rôle (re-review 10/08, M4) : si le copilote
   * échoue après le pilote, le retry réutilise l'enregistrement du pilote au
   * lieu d'en créer un doublon — sauf si sa saisie a changé entre-temps.
   */
  const submittedRolesRef = useRef<RoleSubmissionCache>({});

  const fieldsByStep = useMemo(() => groupFieldsByStep(fields), [fields]);
  const fieldIndex = useMemo(() => buildFieldIndex(fields), [fields]);
  const ruleCtx = useMemo(
    () => buildRuleContext(fields, values, deferred),
    [fields, values, deferred]
  );

  const crewNumber = bootstrap?.prefill?.crew?.crew_number ?? null;
  const infoDeadline = bootstrap?.info_deadline ?? '';
  // 00252 : règles du produit (passeport, date butoir) et identité verrouillée (BPI).
  const [eventRules, setEventRules] = useState<BedEventRules | null>(null);
  const [rulesState, setRulesState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [rulesAttempt, setRulesAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setRulesState('loading');
    setEventRules(null);
    void getBedEventRules(token).then((rules) => {
      if (cancelled) return;
      setEventRules(rules);
      setRulesState(rules ? 'ready' : 'error');
    });
    return () => {
      cancelled = true;
    };
  }, [token, rulesAttempt]);
  const [lockedFieldIds, setLockedFieldIds] = useState<Set<string>>(() => new Set());

  // -------------------------------------------------------------------
  // RÔLES PRÉSENTS
  // -------------------------------------------------------------------
  // Le copilote n'est proposé à la signature/au certificat que s'il existe
  // vraiment (prefill CRM ou saisie manuelle) — sinon on n'exige rien de lui.
  const roles: BedParticipantRole[] = useMemo(() => {
    const list: BedParticipantRole[] = ['pilot'];
    const hasCopilot =
      Boolean(bootstrap?.prefill?.copilot) ||
      fields.some((f) => f.target_role === 'copilot' && String(values[f.id] ?? '').trim() !== '');
    if (hasCopilot) list.push('copilot');
    return list;
  }, [bootstrap, fields, values]);

  const roleLabels: Record<BedParticipantRole, string> = { pilot: 'Pilote', copilot: 'Copilote' };

  /**
   * Recette 4L du 22/09 (retour 37) : « signature — pilote : ajouter nom et
   * prénom ». On lit l'identité déjà saisie dans le bulletin (champs mappés
   * first_name / last_name du rôle) — aucune requête de plus. Vide tant que le
   * participant n'a rien saisi : on n'invente pas un nom sous une signature.
   */
  const roleNames = useMemo(() => {
    const out: Partial<Record<BedParticipantRole, string>> = {};
    for (const role of ['pilot', 'copilot'] as BedParticipantRole[]) {
      const full = participantFullName(fields, values, role);
      if (full) out[role] = full;
    }
    return out;
  }, [fields, values]);

  // -------------------------------------------------------------------
  // MONTANTS
  // -------------------------------------------------------------------
  const optionalItems = useMemo(() => bootstrap?.optional_items ?? [], [bootstrap]);
  const mandatoryItems = useMemo(() => bootstrap?.mandatory_items ?? [], [bootstrap]);

  /**
   * 00249 — ce que la réédition ne peut PLUS changer. Décision SERVEUR
   * (`get_bed_bootstrap`), jamais devinée ici : le socle 00166 refuserait la
   * nouvelle sélection d'options, et le montant choisi est la promesse dont le
   * tunnel soustrait le déjà-encaissé (00240). Ces verrous ne concernent que la
   * réédition : une première soumission n'a encore rien engagé.
   */
  const optionsLocked = isResubmitting && Boolean(bootstrap?.options_locked);
  // 00280 : PREMIER envoi alors que le dossier de paiement est déjà engagé par
  // le participant : le serveur le dit dès le chargement (options déjà au
  // dossier), pour que le verrou s'applique AVANT les soumissions et les mails.
  const engagedOptionIds =
    !isResubmitting && !bootstrap?.draft?.submitted_at
      ? (bootstrap?.first_submission_locked_option_ids ?? null)
      : null;
  const amountLocked = isResubmitting && Boolean(bootstrap?.amount_locked);

  const selectedOptions: BedPricingItem[] = useMemo(
    () => optionalItems.filter((item) => selectedOptionIds.includes(item.pricing_item_id)),
    [optionalItems, selectedOptionIds]
  );

  const amounts: BedRecapAmounts | null = useMemo(() => {
    const solde = bootstrap?.solde;
    if (!solde) return null;
    return {
      prixTotalCents: solde.prix_total_cents,
      acompteCents: solde.acompte_cents,
      reductionCents: solde.reduction_cents ?? 0,
      reductionLabel: solde.reduction_label ?? null,
      soldeCents: solde.solde_cents,
      // 00269 (retour 35) : encaissements réels déjà reçus sur le solde BED
      // (payment_attempts.succeeded). Absent d'un serveur pas encore migré → 0.
      paiementsPartielsCents: solde.paiements_partiels_cents ?? 0,
    };
  }, [bootstrap]);

  // 00296 : solde vivant nul ou négatif et aucun dossier de paiement : les
  // options payantes ne sont pas proposées (une option exigerait un dossier).
  // Avec un dossier, ses options restent visibles et modifiables comme avant.
  const soldeCoveredAtLoad =
    (amounts?.soldeCents ?? 1) <= 0 &&
    !bootstrap?.draft?.payment_case_id &&
    !bootstrap?.resume_payment?.available;
  // Revue du 09/10 : la validation sans paiement n'est PROMISE que si le
  // serveur l'accepte ; sinon le staff finalise (chemin d'avant).
  const nothingDueAtLoad = soldeCoveredAtLoad && bootstrap?.free_finalization_allowed === true;

  const totalCents = useMemo(() => {
    if (!amounts) return 0;
    return computeTotalCents({
      soldeCents: amounts.soldeCents,
      optionsCents: selectedOptions.reduce((sum, item) => sum + item.amount_cents, 0),
    });
  }, [amounts, selectedOptions]);

  // -------------------------------------------------------------------
  // ÉTATS DES ÉTAPES
  // -------------------------------------------------------------------

  /** Une case « j'ai pris connaissance » manque-t-elle ? */
  const documentsPending = useMemo(
    () =>
      mandatoryItems.some((item) => !isConditionsAcknowledged(item, acknowledged)) ||
      selectedOptions.some((item) => !isConditionsAcknowledged(item, acknowledged)),
    [mandatoryItems, selectedOptions, acknowledged]
  );
  const doubleRoomMissing = doubleRoomAnswer === null;
  /** Étape ⑤ bloquée : un document non lu ou la chambre double sans réponse. */
  const optionsBlocked = documentsPending || doubleRoomMissing;

  const signaturesPending = useMemo(
    () => roles.some((role) => !signatures[role]?.data_url),
    [roles, signatures]
  );
  /** Un participant n'a ni certificat ni engagement à le fournir. */
  const certificatesPending = useMemo(
    () => roles.some((role) => !certificates[role] && !deferred[deferralKey('certificat', role)]),
    [roles, certificates, deferred]
  );
  /** Étape ⑥ bloquée : une signature ou un certificat manque. */
  const signaturesMissing = signaturesPending || certificatesPending;

  // 00252 (retour 10) : passeport expirant avant la fin de l'événement = invalide.
  const invalidFieldIds = useMemo(
    () =>
      fields
        .filter(
          (f) =>
            isPassportExpiryField(f) &&
            isFieldVisible(f, ruleCtx) &&
            !isGroupDeferred(ruleCtx, 'passport', roleKeyOf(f.target_role)) &&
            passportExpiryError(values[f.id] ?? '', eventRules?.passport_min_expiry ?? null) !==
              null
        )
        .map((f) => f.id),
    [fields, values, ruleCtx, eventRules]
  );

  const stepStates = useMemo(() => {
    const states = {} as Record<BedStepId, BedStepState>;
    for (const step of BED_STEPS) {
      const stepIds = new Set(fieldsByStep[step.id].map((f) => f.id));
      states[step.id] = computeStepState(step.id, fieldsByStep[step.id], ruleCtx, {
        optionsBlocked: step.id === 5 ? optionsBlocked : undefined,
        signaturesMissing: step.id === 6 ? signaturesMissing : undefined,
        extraDeferrals:
          step.id === 6 ? roles.map((role) => deferralKey('certificat', role)) : undefined,
        invalidFieldIds: invalidFieldIds.filter((id) => stepIds.has(id)),
      });
    }
    return states;
  }, [fieldsByStep, ruleCtx, optionsBlocked, signaturesMissing, roles, invalidFieldIds]);

  /** Levées manuelles du staff, telles que le bootstrap les a rendues (00248). */
  const resolvedItemCodes = useMemo(
    () => bootstrap?.resolved_item_codes ?? [],
    [bootstrap?.resolved_item_codes]
  );

  /**
   * Liste affichée : celle du serveur tant qu'elle est fraîche, sinon le
   * miroir client (mêmes règles) pour un retour immédiat entre deux
   * enregistrements.
   *
   * Les LEVÉES du staff sont retirées du résultat (revue adversariale 09/2026).
   * Le miroir client les ignore, et la liste que renvoie `save_bed_draft` est
   * volontairement BRUTE : sans ce filtre, l'écran annonçait « Votre bulletin
   * est complet et enregistré » (liste effective) puis, deux lignes plus bas,
   * « 1 élément à fournir · Passeport — Pilote » (miroir client), le report
   * étant justement CONSERVÉ par `deferralsToKeepOnResubmit`. Une liste déjà
   * effective (bootstrap d'un bulletin soumis, retour de finalisation) traverse
   * sans changement.
   */
  const missingItemsRaw = useMemo(() => {
    const local = computeMissingItems(fields, values, deferred);
    if (!serverMissing) return local;
    // Le serveur ne connaît ni les signatures ni le certificat : on garde sa
    // liste (qui fait foi sur les champs) et on la complète des reports
    // hors-formulaire déclarés localement.
    const serverCodes = new Set(serverMissing.map((item) => item.code));
    return [...serverMissing, ...local.filter((item) => !serverCodes.has(item.code))];
  }, [fields, values, deferred, serverMissing]);

  const missingItems = useMemo(
    () => excludeResolvedMissingItems(missingItemsRaw, resolvedItemCodes),
    [missingItemsRaw, resolvedItemCodes]
  );

  // -------------------------------------------------------------------
  // CHARGEMENT
  // -------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const result = await getBedBootstrap(token);
      if (cancelled) return;

      if (result.status !== 'ok') {
        setLoadError(result.status === 'error' ? 'error' : (result.status as LoadError));
        setPhase('ready');
        return;
      }

      const data = result.data;
      setBootstrap(data);

      // Pré-remplissage CRM d'abord, brouillon ENSUITE : ce que le participant
      // a saisi prime toujours sur ce que le CRM croit savoir.
      const prefilled = buildBedPrefillValues(fields, data.prefill);
      const draftValues = data.draft?.values ?? {};
      setValues({ ...prefilled, ...draftValues });
      // 00252 (retour 1) : prénom / nom / date de naissance fournis par le BPI
      // → lecture seule ; un champ vide (copilote) reste saisissable.
      const locked = new Set<string>();
      for (const f of fields) {
        if (
          LOCKED_CONTACT_MAPS.includes(f.contact_field_map ?? '') &&
          String(prefilled[f.id] ?? '').trim() !== ''
        ) {
          locked.add(f.id);
        }
      }
      setLockedFieldIds(locked);
      // Brouillon enregistré avec un certificat ET son report : la case n'est
      // plus affichée, le participant ne pourrait pas la décocher.
      const draftDeferred = data.draft?.deferred ?? {};
      const deferredAtLoad = dropCoveredCertificateDeferrals(
        draftDeferred,
        data.draft?.medical_certificates ?? {}
      );
      setDeferred(deferredAtLoad);
      setSignatures(data.draft?.signatures ?? {});
      setCertificates(data.draft?.medical_certificates ?? {});
      // 00280 : dossier déjà engagé au premier envoi — les options du dossier
      // sont facturées quoi qu'il arrive : on les affiche cochées.
      const draftSelection = data.draft?.options?.selected_item_ids ?? [];
      const engagedAtLoad = !data.draft?.submitted_at
        ? (data.first_submission_locked_option_ids ?? null)
        : null;
      // 00296 : solde vivant nul ou négatif et aucun dossier : aucune option
      // payante n'est proposée (elle exigerait un dossier de paiement), rien n'est
      // présélectionné. Avec un dossier, les options souscrites restent visibles.
      const nothingDueNow =
        (data.solde?.solde_cents ?? 1) <= 0 &&
        !data.draft?.payment_case_id &&
        !data.resume_payment?.available;
      setSelectedOptionIds(
        nothingDueNow
          ? []
          : engagedAtLoad
            ? [...new Set([...draftSelection, ...engagedAtLoad])]
            : draftSelection
      );
      setAcknowledged(data.draft?.options?.acknowledged ?? {});
      setDoubleRoomAnswer(data.draft?.options?.double_room ?? null);
      setAmountToPayCents(data.draft?.amount_to_pay_cents ?? null);
      // 00249 : sur un bulletin SOUMIS, ce qui fait foi est la liste EFFECTIVE
      // (l'archive moins les pièces que le staff a levées) — sinon on réclame au
      // participant un passeport déjà reçu par mail, et l'écran contredit le
      // statut de sa fiche équipage. Logique pure, testée dans wizard-model.
      const submittedMissing = data.draft?.submitted_at
        ? pickEffectiveMissingItems(data.draft.missing_items, data.effective_missing_items)
        : (data.draft?.missing_items ?? null);
      // Report retiré au chargement d'un brouillon non soumis : la liste
      // enregistrée le réclame encore, le miroir local fait foi jusqu'à la
      // prochaine sauvegarde. Un bulletin soumis garde la liste du serveur.
      setServerMissing(
        !data.draft?.submitted_at && deferredAtLoad !== draftDeferred ? null : submittedMissing
      );
      setIsReturning(Boolean(data.draft));
      setResumeState(data.resume_payment ?? null);
      setResubmitAllowed(Boolean(data.resubmit_allowed));

      if (data.draft?.submitted_at) {
        const effective = submittedMissing ?? [];
        setPhase('submitted');
        setOutcome({
          complete: effective.length === 0,
          paymentCaseId: data.draft.payment_case_id,
          missingCount: effective.length,
          amountToPayCents: data.draft.amount_to_pay_cents,
          degraded: null,
          // 00296 : un bulletin soumis SANS dossier a été finalisé « rien à
          // payer » et le reste tant qu'aucun dossier n'existe (le CRM dit la
          // même chose) : seule une reprise de paiement proposée l'emporte.
          // 00304 : un dossier existe mais une remise posée après coup couvre
          // le solde : rien à payer non plus, jamais « Reprendre le paiement ».
          // 00305 : sans dossier, un solde de nouveau dû (réduction retirée,
          // grille modifiée) n'est plus « rien à payer ».
          nothingDue:
            !data.resume_payment?.debt_reopened &&
            ((data.draft.payment_case_id === null && !data.resume_payment?.available) ||
              Boolean(data.resume_payment?.nothing_due)),
          // 00312 (TICKET-DEROG-04) : dérogation d'âge à valider, relue par le
          // serveur à chaque chargement.
          derogationPending: Boolean(data.resume_payment?.derogation_pending),
        });
        setCurrentStep(BED_LAST_STEP);
        return;
      }

      setPhase('ready');
    })();

    return () => {
      cancelled = true;
    };
    // `fields` est stable une fois le formulaire chargé (PublicFormPage le fige).
  }, [token, fields]);

  /**
   * Atterrissage (cahier L94) : à la reprise d'un brouillon, on ouvre la
   * première étape incomplète — et directement le récapitulatif si tout est
   * complet. Calculé une seule fois, après le chargement.
   */
  const landedRef = useRef(false);
  useEffect(() => {
    if (landedRef.current || phase !== 'ready' || rulesState !== 'ready' || !bootstrap) return;
    landedRef.current = true;
    if (!bootstrap.draft) return;
    setCurrentStep(landingStep(stepStates));
  }, [phase, rulesState, bootstrap, stepStates]);

  // -------------------------------------------------------------------
  // ÉVÉNEMENT `loaded`
  // -------------------------------------------------------------------
  const loadedNotifiedRef = useRef(false);
  useEffect(() => {
    if (loadedNotifiedRef.current || !bootstrap) return;
    loadedNotifiedRef.current = true;
    notifyParentBedState({
      state: 'loaded',
      step: currentStep,
      crewNumber,
      // 00249 : la liste EFFECTIVE, comme partout ailleurs — le parent ne doit
      // pas afficher un élément que l'administration a déjà levé.
      missingItems: pickEffectiveMissingItems(
        bootstrap.draft?.missing_items,
        bootstrap.effective_missing_items
      ),
      // 00232 : statut CRM après l'ouverture du lien — `get_bed_bootstrap` a
      // déjà posé « BED téléchargé » si le porteur était pré-inscrit.
      bedStatus: bootstrap.bed_status,
    });
  }, [bootstrap, crewNumber, currentStep]);

  // -------------------------------------------------------------------
  // AUTO-SAVE
  // -------------------------------------------------------------------

  /**
   * Rend les SUPPRESSIONS explicites : un rôle du parcours qui n'a plus de
   * valeur locale part à `null`, ce que le serveur interprète comme « retire
   * cette clé » (bed_merge_patch_map, 00231).
   *
   * Sans ça, retirer une signature ou un certificat ne faisait qu'omettre la
   * clé du patch — et le merge `ancien || patch` conservait l'ancienne valeur,
   * qui réapparaissait au rechargement.
   */
  const withExplicitDeletions = <T,>(
    map: Partial<Record<BedParticipantRole, T>>
  ): Record<string, T | null> => Object.fromEntries(roles.map((role) => [role, map[role] ?? null]));

  /**
   * Certificats retirés dont le fichier attend sa purge (re-review 10/08) :
   * on n'efface l'objet du bucket qu'APRÈS qu'une sauvegarde a persisté le
   * retrait — avant, la purge partait immédiatement et
   * `bed_certificate_purgeable` (00239) la refusait (`still_referenced`, le
   * brouillon serveur référençait encore le fichier au moment du clic) : le
   * fichier n'était en pratique JAMAIS effacé.
   */
  const pendingPurgesRef = useRef<string[]>([]);

  /** Snapshot courant, lu par la sauvegarde sans la faire dépendre du rendu. */
  const snapshotRef = useRef<BedDraftPatch>({});
  snapshotRef.current = {
    values,
    deferred,
    step_states: stepStates,
    last_step: currentStep,
    signatures: withExplicitDeletions(signatures),
    options: {
      selected_item_ids: selectedOptionIds,
      acknowledged,
      double_room: doubleRoomAnswer ?? undefined,
    },
    medical_certificates: withExplicitDeletions(certificates),
    amount_to_pay_cents: amountToPayCents,
  };

  /**
   * File de sérialisation des sauvegardes : deux `persist` concurrents
   * (auto-save périodique, changement d'étape, retrait de certificat)
   * pourraient sinon commiter dans le désordre — le plus ancien réécrirait un
   * certificat retiré APRÈS que sa purge a effacé le fichier du bucket.
   * Chaque sauvegarde attend la précédente et relit le snapshot à SON tour.
   */
  const persistChainRef = useRef<Promise<boolean>>(Promise.resolve(true));
  /** Dernier refus du serveur à l'enregistrement (00248 : verrou de réédition). */
  const lastSaveErrorRef = useRef<BedTokenError | null>(null);

  const persist = useCallback(
    (options: { emitStep?: BedStepId } = {}) => {
      const run = async (): Promise<boolean> => {
        if (phase === 'submitted' || phase === 'submitting') return true;
        setSaveState('saving');

        const snapshot = snapshotRef.current;
        const result = await saveBedDraft(token, snapshot);

        if (result.status !== 'ok') {
          lastSaveErrorRef.current = result.status;
          setSaveState('error');
          return false;
        }
        lastSaveErrorRef.current = null;

        // Purges en attente : le brouillon vient d'être enregistré — on
        // n'efface que les fichiers que CE snapshot ne référence plus (un
        // retrait pas encore re-rendu au moment d'un auto-save reste en file).
        // Best-effort : un échec de purge ne bloque jamais la sauvegarde.
        const savedPaths = new Set(
          Object.values(snapshot.medical_certificates ?? {})
            .map((cert) => cert?.path)
            .filter(Boolean)
        );
        const flushable = pendingPurgesRef.current.filter((path) => !savedPaths.has(path));
        pendingPurgesRef.current = pendingPurgesRef.current.filter((path) => savedPaths.has(path));
        for (const path of flushable) void purgeBedCertificate(token, path);

        setSaveState('saved');
        setLastSavedAt(result.savedAt);
        setServerMissing(
          snapshot.values === snapshotRef.current.values &&
            snapshot.deferred === snapshotRef.current.deferred
            ? result.missingItems
            : null
        );
        setIsReturning(true);

        if (options.emitStep !== undefined) {
          notifyParentBedState({
            state: 'step_saved',
            step: options.emitStep,
            crewNumber,
            missingItems: result.missingItems,
          });
        }
        return true;
      };

      const chained = persistChainRef.current.then(run, run);
      persistChainRef.current = chained;
      return chained;
    },
    [phase, token, crewNumber]
  );

  // Sauvegarde périodique en arrière-plan : une fermeture d'onglet inopinée ne
  // coûte au pire que les 20 dernières secondes de saisie.
  useEffect(() => {
    if (phase !== 'ready') return;
    const handle = window.setInterval(() => {
      void persist();
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [phase, persist]);

  // -------------------------------------------------------------------
  // NAVIGATION
  // -------------------------------------------------------------------
  const goToStep = useCallback(
    async (target: BedStepId, opts: { save?: boolean } = { save: true }) => {
      if (opts.save !== false) await persist({ emitStep: currentStep });
      setShowErrors(false);
      setCurrentStep(target);
      // Sur mobile, on remonte : sinon on atterrit au milieu de l'étape suivante.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Recette 4L du 22/09 (retour 31) : embarqué sur le site 4L, c'est la
      // page HÔTE qui défile — on lui demande de remonter au formulaire.
      // Émis ici, sur une navigation du participant, et non à chaque
      // changement d'étape : l'atterrissage au chargement change aussi
      // l'étape, et la page sauterait d'elle-même au formulaire, par-dessus
      // l'introduction.
      notifyParentScrollToTop();
    },
    [persist, currentStep]
  );

  const handleNext = async () => {
    if (rulesState !== 'ready') return;
    if (!canLeaveStep(stepStates[currentStep], fieldsByStep[currentStep], ruleCtx)) {
      setShowErrors(true);
      await persist({ emitStep: currentStep });
      return;
    }
    if (currentStep < BED_LAST_STEP) {
      await goToStep((currentStep + 1) as BedStepId);
    }
  };

  const handlePrevious = async () => {
    if (currentStep > 1) await goToStep((currentStep - 1) as BedStepId);
  };

  // -------------------------------------------------------------------
  // MUTATIONS
  // -------------------------------------------------------------------
  const handleFieldChange = useCallback(
    (fieldId: string, value: string) => {
      setServerMissing(null);
      setSaveState('idle');
      setValues((prev) => {
        const next = { ...prev, [fieldId]: value };

        // Bascule étudiant / jeune actif : on VIDE le champ devenu sans objet.
        // Sans cela, une profession pré-remplie par le CRM survit à un
        // re-basculement en « étudiant », et le trigger SQL
        // `create_contact_from_submission` — qui déduit « plus étudiant » de la
        // seule présence d'une profession — refuse alors de rattacher l'école.
        const role = Object.entries(fieldIndex.student).find(([, f]) => f.id === fieldId)?.[0];
        if (role) {
          const becameNotStudent = value === STUDENT_TRUE_VALUE;
          const toClear = becameNotStudent
            ? [fieldIndex.school[role], fieldIndex.schoolCity[role]]
            : [fieldIndex.profession[role]];
          toClear.forEach((f) => {
            if (f) next[f.id] = '';
          });
        }

        // Recette 4L du 22/09 (retour 3) : saisir une école vide la profession,
        // symétrique du vidage ci-dessus. Sans cela le champ disparaît de
        // l'écran mais sa valeur part quand même au serveur, qui en déduit
        // « plus étudiant » et laisse tomber l'école tout juste saisie.
        const schoolRole = Object.entries(fieldIndex.school).find(([, f]) => f.id === fieldId)?.[0];
        if (schoolRole && value.trim() !== '') {
          const professionField = fieldIndex.profession[schoolRole];
          if (professionField) next[professionField.id] = '';
        }

        // 00252 (retour 18) : décocher « pas propriétaire » vide nom/prénom du propriétaire.
        if (fieldIndex.notOwner && fieldId === fieldIndex.notOwner.id && value === '') {
          fieldIndex.ownerFields.forEach((f) => {
            next[f.id] = '';
          });
        }

        return next;
      });
    },
    [fieldIndex]
  );

  // 00252 (retour 17) : immatriculation connue du CRM → date de 1ère mise en
  // circulation pré-remplie (si le participant ne l'a pas déjà saisie).
  const plateField = useMemo(
    () => fieldsByStep[4].find((f) => f.order_index === OI_VEHICLE_PLATE) ?? null,
    [fieldsByStep]
  );
  const plateDateField = useMemo(
    () => fieldsByStep[4].find((f) => f.order_index === OI_VEHICLE_DATE) ?? null,
    [fieldsByStep]
  );
  const plateValue = plateField ? (values[plateField.id] ?? '') : '';
  useEffect(() => {
    if (!plateField || !plateDateField || phase !== 'ready') return;
    const normalized = normalizeRegistration(plateValue);
    if (normalized.length < 4) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const hint = await getBedVehicleHint(token, normalized);
      if (cancelled || !hint) return;
      setValues((prev) => {
        if (String(prev[plateDateField.id] ?? '').trim() !== '') return prev;
        return { ...prev, [plateDateField.id]: hint };
      });
    }, 600);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plateValue, plateField, plateDateField, token]);

  // 00252 (retour 32) : à chaque changement d'étape, la page remonte en haut du
  // formulaire (après rendu — le scroll « smooth » de goToStep part avant).
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentStep]);

  const handleToggleDeferral = (group: BedDeferralGroup, role: string, on: boolean) => {
    setServerMissing(null);
    setSaveState('idle');
    const key = deferralKey(group, role);
    setDeferred((prev) => {
      const next = { ...prev };
      if (on) next[key] = { due_date: infoDeadline };
      else delete next[key];
      return next;
    });
  };

  const handleToggleOption = (item: BedPricingItem, selected: boolean) => {
    setSelectedOptionIds((prev) =>
      selected
        ? [...new Set([...prev, item.pricing_item_id])]
        : prev.filter((id) => id !== item.pricing_item_id)
    );
  };

  const handleToggleAck = (item: BedPricingItem, acked: boolean) => {
    setAcknowledged((prev) => {
      const next = { ...prev };
      // On mémorise l'URL AFFICHÉE : le socle 00166 n'accepte la preuve que si
      // elle correspond au document courant (anti-course sur la rotation).
      if (acked && item.conditions_url) next[item.pricing_item_id] = item.conditions_url;
      else delete next[item.pricing_item_id];
      return next;
    });
    // Décocher le document retire l'option : on ne garde jamais une option
    // souscrite sans preuve de lecture. 00249 : sauf quand la sélection est
    // figée par un paiement en cours — la retirer ferait refuser la
    // re-soumission (`bed_options_locked_on_resubmit`) pour un geste qui ne
    // pouvait de toute façon rien changer au dossier.
    if (!acked && !optionsLocked && !engagedOptionIds?.includes(item.pricing_item_id)) {
      setSelectedOptionIds((prev) => prev.filter((id) => id !== item.pricing_item_id));
    }
  };

  const handleSignature = (role: BedParticipantRole, dataUrl: string | null) => {
    setSignatures((prev) => {
      const next = { ...prev };
      if (dataUrl) next[role] = { data_url: dataUrl, signed_at: new Date().toISOString() };
      else delete next[role];
      return next;
    });
  };

  const handleCertificate = (role: BedParticipantRole, certificate: BedCertificate | null) => {
    const removed = certificate ? null : (certificates[role] ?? null);

    setCertificates((prev) => {
      const next = { ...prev };
      if (certificate) next[role] = certificate;
      else delete next[role];
      return next;
    });
    if (certificate) {
      // Comme un décochage (handleToggleDeferral) : la liste serveur réclame
      // encore le certificat jusqu'à la prochaine sauvegarde, le miroir local
      // prend le relais.
      setServerMissing(null);
      setSaveState('idle');
      setDeferred((prev) => dropCoveredCertificateDeferrals(prev, { [role]: certificate }));
    }

    // Le fichier part AVEC la référence : une donnée de santé retirée du
    // bulletin n'a rien à faire dans le bucket. La suppression passe par une
    // edge function service_role — Supabase interdit le DELETE SQL sur
    // storage.objects, et le participant n'a aucun droit de suppression
    // (00235). La purge est MISE EN FILE et exécutée par la prochaine
    // sauvegarde réussie (re-review 10/08) : au moment du clic le retrait
    // n'est PAS encore persisté et `bed_certificate_purgeable` (00239) REFUSE
    // de purger un chemin encore référencé — la purge immédiate était donc
    // systématiquement rejetée et le fichier restait pour toujours.
    if (removed?.path) {
      pendingPurgesRef.current.push(removed.path);
      // Au tick suivant : le snapshot est réécrit AU RENDU, une sauvegarde
      // lancée dans le handler repartirait avec le certificat encore dedans
      // (et la purge resterait en file jusqu'à l'auto-save).
      window.setTimeout(() => void persist(), 0);
    }
  };

  // -------------------------------------------------------------------
  // SOUMISSION
  // -------------------------------------------------------------------
  const buildSubmissionData = (role: 'pilot' | 'copilot') => {
    const data: Record<string, unknown> = {};
    for (const field of fields) {
      const isRole = field.target_role === role;
      const isCommon = !field.target_role;
      if (!isRole && !isCommon) continue;
      if (values[field.id] !== undefined) data[field.id] = values[field.id];
    }

    // Revue du 22/09 (retour 3) : une profession MASQUÉE — parce qu'une école
    // est renseignée — ne doit pas partir au serveur. Le vidage de
    // handleFieldChange ne couvre que la saisie ; une profession PRÉ-REMPLIE
    // depuis le CRM survivait, et `create_contact_from_submission` (00052) en
    // déduisait « plus étudiant » puis ignorait l'école que le participant
    // voyait à l'écran.
    Object.assign(data, hiddenValuesToClear(ruleCtx, role));

    // Métadonnées BED sous un préfixe réservé : le trigger
    // `create_contact_from_submission` n'itère que sur les `form_fields`, ces
    // clés lui sont donc invisibles. C'est là que la vague 2 (zone admin) ira
    // lire les éléments manquants, la signature et les engagements de date.
    data.__bed_role = role;
    data.__bed_crew_number = crewNumber;
    data.__bed_deferred = deferred;
    // (revue 09/2026) BRUTE, jamais filtrée des levées : l'archive est la
    // matière que le snapshot annote côté CRM (00232 : repli sur la dernière
    // soumission quand aucun brouillon n'existe). Une archive pré-filtrée
    // rendrait la levée du staff inatteignable depuis la fiche équipage.
    data.__bed_missing_items = missingItemsRaw;
    data.__bed_signature = signatures[role] ?? null;
    data.__bed_medical_certificate = certificates[role] ?? null;
    data.__bed_selected_option_ids = selectedOptionIds;
    data.__bed_acknowledged_conditions = acknowledged;
    data.__bed_double_room = doubleRoomAnswer;
    data.__bed_amount_to_pay_cents = amountToPayCents ?? totalCents;

    return data;
  };

  /**
   * Rouvre un bulletin soumis pour le compléter (00248). Les reports « je
   * fournirai avant le … » sont DÉCOCHÉS pour que les éléments manquants
   * ressortent à chaque passage (demande recette du 04/09) — sauf ceux que le
   * staff a déjà levés côté CRM, qu'on ne redemande pas au participant.
   *
   * Le bootstrap est RELU d'abord (revue adversariale 09/2026) : après une
   * première soumission puis un paiement dans le même montage d'iframe, les
   * verrous du chargement initial (`amount_locked`, `options_locked`) sont
   * périmés — le champ montant s'ouvrait alors que le serveur l'avait figé, et
   * les levées posées entre-temps manquaient. Échec de relecture : on garde les
   * valeurs en main et on ouvre quand même la réédition (le serveur refusera
   * proprement à l'enregistrement, message déjà géré).
   */
  const handleStartResubmit = async () => {
    if (!resubmitAllowed || resubmitBusy) return;
    setResubmitBusy(true);
    setResubmitError(null);

    const refreshed = await getBedBootstrap(token);
    let resolved = bootstrap?.resolved_item_codes ?? [];

    if (refreshed.status === 'ok') {
      setBootstrap(refreshed.data);
      setResumeState(refreshed.data.resume_payment ?? null);
      resolved = refreshed.data.resolved_item_codes ?? [];
      // (revue 09/2026) Le serveur a pu RÉALIGNER la sélection d'options
      // archivée sur celle du dossier (00249, dossier engagé). Sans cette
      // resynchronisation, le wizard garderait en mémoire une sélection
      // périmée : sa re-soumission suivante serait refusée pour un geste que
      // le participant n'a jamais fait. Idem pour la certification et le
      // montant, que le serveur peut avoir figés.
      setSelectedOptionIds(refreshed.data.draft?.options?.selected_item_ids ?? []);
      setAcknowledged(refreshed.data.draft?.options?.acknowledged ?? {});
      setAmountToPayCents(refreshed.data.draft?.amount_to_pay_cents ?? null);

      // Le droit de rééditer a pu tomber entre l'affichage de l'écran et le
      // clic (staff qui valide le dossier). On ne fait pas entrer le
      // participant dans un formulaire dont l'envoi serait refusé.
      if (!refreshed.data.resubmit_allowed) {
        setResubmitAllowed(false);
        setResubmitBusy(false);
        setResubmitError(
          'Votre dossier a été validé entre-temps : le bulletin ne peut plus être modifié.'
        );
        return;
      }
    }

    setDeferred((prev) => deferralsToKeepOnResubmit(prev, resolved));
    // La liste archivée par le serveur reflète l'ancienne saisie : on repart du
    // miroir client jusqu'au prochain enregistrement.
    setServerMissing(null);
    setIsResubmitting(true);
    setShowErrors(false);
    setSubmitError(null);
    setResubmitBusy(false);
    // Ré-atterrir sur la première étape incomplète, comme à une reprise.
    landedRef.current = false;
    setPhase('ready');
  };

  const handleSubmit = async () => {
    if (phase === 'submitting' || rulesState !== 'ready') return;
    if (signaturesMissing || optionsBlocked) {
      setShowErrors(true);
      return;
    }
    // 00280 : option ajoutée à un dossier déjà engagé — refusée par le serveur.
    // On s'arrête AVANT d'enregistrer les soumissions et d'envoyer les mails.
    if (engagedOptionIds && selectedOptionIds.some((id) => !engagedOptionIds.includes(id))) {
      setSubmitError(OPTION_LOCKED_FIRST_SUBMISSION_MESSAGE);
      return;
    }

    setPhase('submitting');
    setSubmitError(null);

    // Le brouillon est enregistré AVANT toute écriture définitive : si la
    // chaîne de paiement échoue, la saisie n'est jamais perdue.
    const saved = await persist();

    // 00248 : en réédition, un enregistrement refusé (dossier validé entre-temps
    // par le staff, ou erreur) arrête tout ICI — avant de créer des soumissions
    // et d'envoyer des notifications que la finalisation refuserait ensuite.
    if (isResubmitting && !saved) {
      setSubmitError(
        lastSaveErrorRef.current === 'already_submitted'
          ? 'Votre dossier a été validé entre-temps : le bulletin ne peut plus être modifié.'
          : 'Votre mise à jour n’a pas pu être enregistrée. Vos réponses sont conservées : réessayez dans un instant.'
      );
      setPhase('ready');
      return;
    }

    try {
      const submissionIds: string[] = [];

      for (const role of roles) {
        const submissionData = buildSubmissionData(role);

        // Retry après un échec partiel (le copilote a échoué APRÈS le pilote) :
        // le rôle déjà enregistré à payload identique est réutilisé tel quel,
        // jamais re-soumis en doublon. L'empreinte EXCLUT __bed_missing_items :
        // le `persist()` en tête de tentative réécrit les manquants recalculés
        // par le serveur — la retry s'invaliderait elle-même et re-soumettrait
        // les rôles déjà passés.
        const stableData = { ...submissionData };
        delete stableData.__bed_missing_items;
        const payload = JSON.stringify(stableData);
        const reusedId = reusableSubmissionId(submittedRolesRef.current, role, payload);
        if (reusedId) {
          submissionIds.push(reusedId);
          continue;
        }

        // 00192 : le bulletin porte passeport, permis, CEAM, contact d'urgence
        // et signature. Il ne part plus par un INSERT anon direct (la policy
        // `form_submissions_anon_all` les rendait lisibles/modifiables par
        // toute clé anon) mais par une RPC SECURITY DEFINER au retour minimal. La capacité BED
        // est transmise séparément et vérifiée avant toute création de contact.
        const { data: rows, error } = await publicSupabase.rpc('submit_bed_public_form', {
          p_token: token,
          p_form_id: form.id,
          p_data: submissionData,
        });

        if (error) throw error;
        const submissionId = rows[0].id;
        submissionIds.push(submissionId);
        submittedRolesRef.current[role] = { id: submissionId, payload };

        // Notification email : best-effort, exactement comme le renderer public.
        publicSupabase.functions
          .invoke('notify-form-submission', {
            body: {
              form_id: form.id,
              submission_id: submissionId,
              submission_data: submissionData,
            },
          })
          .catch((err) => console.warn('[BED] notification email:', err));
      }

      // 00248 : re-soumission — le dossier de solde existe depuis la première
      // soumission, on ne le recrée JAMAIS (un seul dossier BED par bulletin) :
      // on finalise dessus. `paymentCase` reste null dans ce cas — aucun nouveau
      // jeton de tunnel à annoncer au parent, la reprise du paiement passe par
      // le CTA « Reprendre le paiement ». (Un bulletin en mode dégradé n'a jamais
      // été finalisé : `submitted_at` est NULL, il repasse donc par une première
      // soumission normale au rechargement.)
      const existingCaseId = bootstrap?.draft?.payment_case_id ?? outcome?.paymentCaseId ?? null;
      let paymentCase: BedPaymentCaseData | null = null;
      let paymentCaseId = existingCaseId;
      // 00296 : rien à payer (engagement offert, acompte ou réduction qui
      // couvrent la grille) : pas de dossier, le bulletin se finalise sans.
      let nothingDue = false;

      if (!paymentCaseId) {
        const paymentResult = await createBedPaymentCase({
          formSubmissionId: submissionIds[0],
          bedAccessToken: token,
        });

        if (paymentResult.status === 'nothing_due') {
          nothingDue = true;
        } else if (paymentResult.status !== 'ok') {
          // Le bulletin EST enregistré : on ne le perd pas parce que le solde
          // n'a pas pu être initialisé. On le dit, et on oriente vers le support.
          setOutcome({
            complete: missingItems.length === 0,
            paymentCaseId: null,
            missingCount: missingItems.length,
            amountToPayCents: null,
            degraded: paymentResult.status === 'no_paid_bpi_parent' ? 'no_parent' : 'failed',
            nothingDue: false,
            derogationPending: false,
          });
          setIsResubmitting(false);
          setPhase('submitted');
          // Retour 31 (revue du 23/09, point 3) : l'écran « enregistré » est
          // court et remplace le récapitulatif, le plus long du parcours — sans
          // ce signal, le participant reste en bas du cadre de hauteur fixe.
          notifyParentScrollToTop();
          notifyParentBedState({
            state: missingItems.length === 0 ? 'submitted_complete' : 'submitted_incomplete',
            crewNumber,
            missingItems: missingItems as BedMissingItemPayload[],
          });
          return;
        } else {
          paymentCase = paymentResult.data;
          paymentCaseId = paymentCase.payment_case_id;
        }
      }

      const finalized = await finalizeBedSubmission({
        token,
        paymentCaseId,
        submissionIds,
        optionItemIds: selectedOptionIds,
        conditionsUrls: acknowledged,
        amountToPayCents,
      });

      // 00312 (TICKET-DEROG-04) : rien à payer, mais une dérogation d'âge
      // manque. L'envoi vaut inscription définitive : le serveur ne finalise
      // pas (brouillon conservé, trace pour le staff). Ce n'est pas une panne :
      // écran dédié, sans paiement ni réédition (le lien rouvre le formulaire).
      if (
        (finalized.status === 'ok' && finalized.finalized === false) ||
        finalized.status === 'derogation_pending'
      ) {
        setOutcome({
          complete: missingItems.length === 0,
          paymentCaseId: null,
          missingCount: missingItems.length,
          amountToPayCents: null,
          degraded: null,
          nothingDue: false,
          derogationPending: true,
          notSubmitted: true,
        });
        setResubmitAllowed(false);
        setIsResubmitting(false);
        setPhase('submitted');
        notifyParentScrollToTop();
        return;
      }

      // 00248 : en réédition, un refus du serveur (dossier validé entre-temps,
      // erreur) ne doit pas afficher un écran « bulletin enregistré » trompeur :
      // on reste sur le formulaire et on le dit.
      if (isResubmitting && finalized.status !== 'ok') {
        setSubmitError(
          finalized.status === 'already_submitted'
            ? 'Votre dossier a été validé entre-temps : le bulletin ne peut plus être modifié.'
            : finalized.status === 'options_locked'
              ? 'Votre sélection d’options ne peut plus être modifiée : votre paiement a commencé. Vos réponses sont conservées.'
              : finalized.status === 'payment_in_progress'
                ? PAYMENT_IN_PROGRESS_MESSAGE
                : finalized.status === 'options_require_payment'
                  ? OPTIONS_REQUIRE_PAYMENT_MESSAGE
                  : finalized.code?.includes('case_mismatch')
                    ? 'Une incohérence a été détectée sur votre dossier. Écrivez-nous à service.administratif@desertours.com.'
                    : 'Votre mise à jour n’a pas pu être enregistrée. Vos réponses sont conservées : réessayez dans un instant.'
        );
        setPhase('ready');
        return;
      }

      // 00280 : au premier envoi, un refus du serveur qui dit quoi faire (option
      // ajoutée entre le chargement et l'envoi à un dossier devenu engagé,
      // paiement CB en cours) n'est pas un succès dégradé : rien n'est
      // enregistré, on reste sur le formulaire, jamais l'écran « bulletin
      // enregistré ».
      if (finalized.status === 'options_locked' || finalized.status === 'payment_in_progress') {
        setSubmitError(
          finalized.status === 'options_locked'
            ? OPTION_LOCKED_FIRST_SUBMISSION_MESSAGE
            : PAYMENT_IN_PROGRESS_MESSAGE
        );
        setPhase('ready');
        return;
      }

      // 00296 : sans dossier de paiement, un refus de finalisation n'a aucun
      // repli (pas de reprise possible) : ce n'est jamais un succès. On reste
      // sur le formulaire et on dit pourquoi.
      if (nothingDue && finalized.status !== 'ok') {
        setSubmitError(
          finalized.status === 'options_require_payment'
            ? OPTIONS_REQUIRE_PAYMENT_MESSAGE
            : NOTHING_DUE_FINALIZE_FAILED_MESSAGE
        );
        setPhase('ready');
        return;
      }

      // BUG-WIZ-11 : tout autre échec de la finalisation au premier envoi (panne
      // réseau, erreur serveur) laisse le brouillon NON finalisé (options et prises
      // de connaissance non rattachées au dossier, statut non recalculé) : jamais
      // l'écran « bulletin enregistré » ni le passage au paiement. Au nouvel essai,
      // les soumissions déjà passées sont réutilisées (submittedRolesRef) et le
      // socle reprend le dossier de solde déjà créé.
      if (finalized.status !== 'ok') {
        setSubmitError(
          finalized.status === 'already_submitted'
            ? 'Votre dossier a été validé entre-temps : le bulletin ne peut plus être modifié.'
            : finalized.status === 'options_require_payment'
              ? OPTIONS_REQUIRE_PAYMENT_MESSAGE
              : 'Votre bulletin n’a pas pu être finalisé. Vos réponses sont enregistrées : réessayez dans un instant.'
        );
        setPhase('ready');
        return;
      }

      const finalMissing =
        finalized.status === 'ok'
          ? (finalized.missingItems as BedMissingItemPayload[])
          : (missingItems as BedMissingItemPayload[]);
      const complete = finalized.status === 'ok' ? finalized.complete : finalMissing.length === 0;
      nothingDue = nothingDue || (finalized.status === 'ok' && finalized.nothingDue);
      const finalAmount = nothingDue
        ? 0
        : finalized.status === 'ok'
          ? finalized.amountToPayCents
          : (amountToPayCents ?? totalCents);
      // Ce que le participant règle maintenant : le RELIQUAT de la promesse
      // cumulée (00240). C'est ce que disent le contrat des messages
      // (BED_IFRAME_EVENTS.md §10) et l'état de reprise (revue du 24/09, point 6).
      const announcedAmount = promiseRemainderCents(
        finalAmount,
        amounts?.paiementsPartielsCents ?? 0
      );
      // 00232 : statut CRM après la transition de soumission, tel que le
      // serveur l'a posé. `null` si la finalisation a échoué — la clé est alors
      // absente du message, jamais devinée côté client.
      const finalStatus = finalized.status === 'ok' ? finalized.bedStatus : null;
      // 00312 (TICKET-DEROG-04) : bulletin enregistré, paiement du solde
      // suspendu (dérogation d'âge à valider) : ni reprise, ni `payment_ready`.
      const derogationPending = finalized.status === 'ok' && finalized.derogationPending;

      setServerMissing(finalMissing);
      setOutcome({
        complete,
        paymentCaseId,
        missingCount: finalMissing.length,
        amountToPayCents: finalAmount,
        degraded: null,
        nothingDue,
        derogationPending,
      });
      // Re-review 10/08 (M2) : hors iframe le `postMessage` de fin est ignoré
      // et le bootstrap n'est relu qu'au prochain chargement — sans cet état,
      // le CTA de reprise n'apparaissait qu'après un refresh. La décision
      // (finalisation OK ? iframe ?) est pure et testée dans wizard-model.
      if (paymentCase && !derogationPending) {
        const postSubmitResume = buildPostSubmitResumeState({
          finalizeOk: finalized.status === 'ok',
          isEmbedded,
          paymentCaseId: paymentCase.payment_case_id,
          orderId: paymentCase.order_id,
          // L'état de reprise porte le RELIQUAT de la promesse (00240), pas la
          // promesse cumulée : les versements déjà encaissés en sont déduits.
          amountToPayCents: announcedAmount,
        });
        if (postSubmitResume) setResumeState(postSubmitResume);
      }
      // 00248 : l'écran « enregistré » propose « Compléter mon bulletin » tant
      // que le statut renvoyé le permet — même règle que le serveur, sans
      // recharger. Sans statut (finalisation KO), on garde la décision du bootstrap.
      if (finalStatus) setResubmitAllowed(isResubmittableStatus(finalStatus));
      // En réédition, on relit ensuite le bootstrap (aucun jeton de tunnel émis ;
      // sa transition « BED téléchargé » est sans objet après soumission) pour
      // rafraîchir la reprise de paiement et le droit de rééditer — la valeur
      // serveur, plus fraîche, a le dernier mot. Sans cela, en iframe, l'écran
      // « enregistré » n'offrait AUCUNE sortie vers le paiement avant un
      // rechargement (buildPostSubmitResumeState rend null en iframe, et il n'y a
      // pas de nouveau `payment_ready` à relayer) ; hors iframe le CTA gardait le
      // montant d'avant la réédition.
      if (!paymentCase && finalized.status === 'ok') {
        const refreshed = await getBedBootstrap(token);
        if (refreshed.status === 'ok') {
          setBootstrap(refreshed.data);
          setResumeState(refreshed.data.resume_payment ?? null);
          setResubmitAllowed(Boolean(refreshed.data.resubmit_allowed));
          // Même raison qu'à l'ouverture de la réédition : la sélection
          // archivée a pu être réalignée sur le dossier pendant la
          // finalisation, l'état local doit suivre.
          setSelectedOptionIds(refreshed.data.draft?.options?.selected_item_ids ?? []);
          setAcknowledged(refreshed.data.draft?.options?.acknowledged ?? {});
          setAmountToPayCents(refreshed.data.draft?.amount_to_pay_cents ?? null);
        }
      }
      setIsResubmitting(false);
      setPhase('submitted');
      // Retour 31 (revue du 23/09, point 3) : même raison que sur le chemin
      // dégradé. En réédition il n'y a pas de `payment_ready` pour emmener le
      // participant ailleurs, et le bouton « Reprendre le paiement » est en
      // haut de l'écran court.
      notifyParentScrollToTop();

      notifyParentBedState({
        state: complete ? 'submitted_complete' : 'submitted_incomplete',
        crewNumber,
        missingItems: finalMissing,
        paymentCaseId: paymentCaseId ?? undefined,
        amountToPayCents: announcedAmount,
        bedStatus: finalStatus,
      });

      // 00248 : en réédition il n'y a pas de nouveau jeton de tunnel — le parent
      // garde celui de la première soumission, la reprise passe par le CTA.
      // 00312 : dérogation d'âge à valider -> la page hôte n'est jamais envoyée
      // vers le tunnel (qui refuserait de toute façon).
      if (paymentCase && !derogationPending) {
        // Rétro-compatibilité : le message historique reste émis À L'IDENTIQUE,
        // le nouvel événement `payment_ready` s'y ajoute.
        notifyParentBedPaymentReady({
          paymentCaseId: paymentCase.payment_case_id,
          product: form.product?.code ?? paymentCase.product_code ?? '',
          bedToken: paymentCase.bed_token,
          orderId: paymentCase.order_id,
        });

        notifyParentBedState({
          state: 'payment_ready',
          crewNumber,
          paymentCaseId: paymentCase.payment_case_id,
          bedToken: paymentCase.bed_token,
          orderId: paymentCase.order_id,
          amountToPayCents: announcedAmount,
          missingItems: finalMissing,
          bedStatus: finalStatus,
        });
      }
    } catch (err) {
      console.error('[BED] Erreur de soumission:', err);
      setSubmitError(
        'Votre bulletin n’a pas pu être envoyé. Vos réponses sont enregistrées : réessayez dans un instant.'
      );
      setPhase('ready');
    }
  };

  /**
   * Reprise du paiement d'un bulletin déjà soumis (P0 « cul-de-sac »).
   *
   * En iframe, on garde le hand-off historique par `postMessage` : la page
   * parente sait router vers le tunnel, c'est le chemin nominal. Hors iframe —
   * exactement le cas signalé par la review — on navigue nous-mêmes vers le
   * tunnel tokenisé côté 4LT, sinon il n'y a aucune sortie.
   */
  const handleResumePayment = async () => {
    if (resumeBusy) return;
    setResumeBusy(true);
    setResumeError(null);

    const result = await resumeBedPayment(token);

    if (result.status === 'already_settled') {
      setResumeState({ available: false, settled: true });
      setResumeBusy(false);
      return;
    }

    // 00304 : la remise couvre le solde depuis le chargement de la page.
    if (result.status === 'nothing_due') {
      setResumeState({ available: false, settled: false, nothing_due: true });
      setOutcome((previous) => (previous ? { ...previous, nothingDue: true } : previous));
      setResumeBusy(false);
      return;
    }

    // 00312 (TICKET-DEROG-04) : une dérogation d'âge manque depuis le
    // chargement (copilote changé, case décochée) : pas de paiement, le message.
    if (result.status === 'derogation_pending') {
      setResumeState({ available: false, settled: false, derogation_pending: true });
      setOutcome((previous) => (previous ? { ...previous, derogationPending: true } : previous));
      setResumeBusy(false);
      return;
    }

    if (result.status !== 'ok') {
      setResumeError(
        'Le paiement n’a pas pu être ouvert. Écrivez-nous à service.administratif@desertours.com.'
      );
      setResumeBusy(false);
      return;
    }

    const resume = result.data;

    notifyParentBedPaymentReady({
      paymentCaseId: resume.payment_case_id ?? '',
      product: form.product?.code ?? '',
      bedToken: resume.bed_token,
      orderId: resume.order_id ?? '',
    });

    notifyParentBedState({
      state: 'payment_ready',
      crewNumber,
      paymentCaseId: resume.payment_case_id,
      bedToken: resume.bed_token,
      orderId: resume.order_id,
      // 00240 : le parent doit recevoir le montant RÉELLEMENT encaissé (le
      // choix du participant), pas le solde entier — même cohérence que le CTA.
      amountToPayCents: resume.amount_to_pay_cents ?? resume.remaining_cents ?? null,
      missingItems: missingItems as BedMissingItemPayload[],
      bedStatus: bootstrap?.bed_status ?? null,
    });

    if (!isEmbedded) {
      try {
        window.location.assign(buildBedPaymentLink(resume.bed_token));
        return;
      } catch (err) {
        // Variable d'origine absente : on le DIT, on ne fabrique pas un lien
        // CRM de repli qui serait un cul-de-sac de plus.
        console.error('[BED] lien de reprise:', err);
        setResumeError(
          'Le lien de paiement n’est pas configuré. Écrivez-nous à service.administratif@desertours.com.'
        );
      }
    }

    setResumeBusy(false);
  };

  // -------------------------------------------------------------------
  // RENDU — champs d'une étape
  // -------------------------------------------------------------------
  const renderStepFields = (step: BedStepId) => {
    const stepFields = fieldsByStep[step].filter((field) => isFieldVisible(field, ruleCtx));
    const grouped = new Map<string, FormFieldWithValues[]>();
    const plain: FormFieldWithValues[] = [];

    for (const field of stepFields) {
      const role = roleKeyOf(field.target_role);
      // 00252 (retour 15) : la case « mon pays ne fournit pas de CEAM » rejoint le
      // bloc CEAM (elle flottait au milieu du formulaire).
      const groupOf = field.bed_deferrable_group
        ? field.bed_deferrable_group
        : fieldIndex.noCeam[role]?.id === field.id
          ? 'ceam'
          : null;
      if (groupOf) {
        const key = `${groupOf}:${role}`;
        const bucket = grouped.get(key) ?? [];
        bucket.push(field);
        grouped.set(key, bucket);
      } else {
        plain.push(field);
      }
    }

    // Rendu ordinaire d'un champ : libellé, saisie, aide et erreur.
    const renderStandardField = (field: FormFieldWithValues) => {
      const required = isFieldRequired(field, ruleCtx);
      const empty = String(values[field.id] ?? '').trim() === '';
      // 00252 (retour 10) : l'invalidité s'affiche tout de suite (alerte visuelle),
      // le « requis » seulement après une tentative de validation.
      const expiryError = isPassportExpiryField(field)
        ? passportExpiryError(values[field.id] ?? '', eventRules?.passport_min_expiry ?? null)
        : null;
      const error =
        expiryError ?? (showErrors && required && empty ? `${field.label} est requis` : null);

      const isNotOwnerBox = fieldIndex.notOwner?.id === field.id;
      // Dérogation d'âge, même règle qu'au BPI : âge à la date de DÉBUT de
      // l'événement (revue 00272 — le BED lisait la fin, et aurait averti un
      // participant qui fête ses 29 ans pendant le raid mais pas au BPI).
      // Pas d'avertissement sur une date VERROUILLÉE : elle vient du BPI, le
      // participant l'y a déjà vu, et il peut depuis avoir obtenu sa
      // dérogation. Le cas visé par l'équipe est le copilote AJOUTÉ au BED
      // (recette 4L du 22/09, ligne 1).
      // TICKET-DEROG-04 : information NON fermable (l'ancienne popin se
      // fermait) : le serveur suspend le paiement du solde tant que la
      // dérogation n'est pas validée, le participant le sait avant d'envoyer.
      const isBirthDateField = field.contact_field_map === 'birth_date';
      const birthValue = String(values[field.id] ?? '');
      const showDerogation =
        isBirthDateField &&
        productHasAgeLimit(form.product) &&
        !lockedFieldIds.has(field.id) &&
        isOver28AtEventDate(birthValue, eventRules?.event_start_date ?? null);
      return (
        <div key={field.id} className="space-y-2">
          <BedFieldInput
            field={field}
            values={values}
            required={required}
            error={error}
            disabled={phase === 'submitting'}
            readOnly={lockedFieldIds.has(field.id)}
            hint={
              field.bed_deferrable_group === 'ceam' && field.field_type !== 'checkbox' ? (
                <BedCeamHint />
              ) : field.order_index === OI_VEHICLE_ENGINE_CODE ? (
                <p className="text-xs text-zinc-500">{ENGINE_CODE_HINT}</p>
              ) : undefined
            }
            onChange={handleFieldChange}
          />
          {isBirthDateField && showDerogation && (
            <p
              role="note"
              className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800"
              data-testid="bed-derogation-field-notice"
            >
              {BED_DEROGATION_FIELD_NOTICE}
            </p>
          )}
          {isNotOwnerBox && isChecked(values, field.id) && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Une <strong>autorisation de conduite du véhicule</strong> signée par son propriétaire
              vous sera demandée pour la France, l’Espagne et le Maroc.{' '}
              <a
                href={OWNER_AUTHORIZATION_PDF}
                target="_blank"
                rel="noreferrer"
                className="font-medium underline underline-offset-2"
              >
                Télécharger le modèle d’autorisation (PDF)
              </a>{' '}
              ou{' '}
              <a
                href={OWNER_AUTHORIZATION_DOCX}
                className="font-medium underline underline-offset-2"
              >
                en version modifiable (Word)
              </a>
              . Rien à téléverser ici.
            </p>
          )}
        </div>
      );
    };

    const renderField = (field: FormFieldWithValues) => {
      // L'école et sa ville sont rendues ensemble (autocomplete + création).
      if (field.contact_field_map === 'school') {
        const role = roleKeyOf(field.target_role);
        const cityField = fieldIndex.schoolCity[role];
        return (
          <BedSchoolField
            key={field.id}
            token={token}
            schoolField={field}
            cityField={cityField}
            schoolValue={values[field.id] ?? ''}
            cityValue={cityField ? (values[cityField.id] ?? '') : ''}
            cityRequired={cityField ? isFieldRequired(cityField, ruleCtx) : false}
            onChange={handleFieldChange}
            disabled={phase === 'submitting'}
          />
        );
      }

      // La ville d'école est déjà affichée par BedSchoolField.
      if (field.contact_field_map === 'school_city') return null;

      // Recette 4L du 22/09 (retour 6) : « mettre édition et numéro sur la même
      // ligne » — les deux moitiés d'une même information se lisent ensemble.
      const role = roleKeyOf(field.target_role);
      const crewNumberField = fieldIndex.previousCrewNumber[role];
      const editionField = fieldIndex.previousEdition[role];
      if (editionField?.id === field.id && crewNumberField) {
        return (
          <div key={field.id} className="grid gap-4 sm:grid-cols-2">
            {renderStandardField(field)}
            {renderStandardField(crewNumberField)}
          </div>
        );
      }
      // Déjà rendu avec l'édition juste au-dessus.
      if (crewNumberField?.id === field.id && editionField) return null;

      return renderStandardField(field);
    };

    // Les groupes reportables se placent après les champs libres, dans l'ordre
    // passeport → permis → CEAM, pour que l'identité vienne d'abord.
    const groupKeys = [...grouped.keys()].sort((a, b) => {
      const ga = DEFERRABLE_ORDER.indexOf(a.split(':')[0] as BedDeferralGroup);
      const gb = DEFERRABLE_ORDER.indexOf(b.split(':')[0] as BedDeferralGroup);
      return ga - gb;
    });

    const renderedPlain = plain.map(renderField);
    // 00252 : à l'étape véhicule, le bloc immatriculation/date vient AVANT la
    // case « pas propriétaire » (sinon la case flotte au-dessus du bloc).
    return (
      <div className="space-y-5">
        {step !== 4 && renderedPlain}

        {groupKeys.map((key) => {
          const [group, role] = key.split(':') as [BedDeferralGroup, string];
          const entry = deferred[key];
          return (
            <BedDeferrableGroup
              key={key}
              group={group}
              role={role}
              deferredUntil={entry?.due_date ?? null}
              defaultDueDate={infoDeadline}
              disabled={phase === 'submitting'}
              onToggle={(on) => handleToggleDeferral(group, role, on)}
            >
              {(grouped.get(key) ?? []).map(renderField)}
            </BedDeferrableGroup>
          );
        })}
        {step === 4 && renderedPlain}
      </div>
    );
  };

  // -------------------------------------------------------------------
  // RENDU — états globaux
  // -------------------------------------------------------------------
  if (phase === 'loading') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-zinc-400" />
          <p className="text-sm text-zinc-600">Chargement de votre bulletin…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <AlertCircle className="mx-auto mb-3 h-8 w-8 text-amber-600" />
        <h2 className="mb-2 text-lg font-semibold text-amber-900">
          {loadError === 'expired' ? 'Ce lien a expiré' : 'Ce lien n’est plus valide'}
        </h2>
        <p className="text-sm text-amber-800">
          Contactez-nous à{' '}
          <a href="mailto:service.administratif@desertours.com" className="font-medium underline">
            service.administratif@desertours.com
          </a>{' '}
          pour recevoir un nouveau lien vers votre bulletin.
        </p>
      </div>
    );
  }

  // Le bulletin n'est pas validable avant le chargement des règles du produit.
  // Une date volontairement absente dans une réponse réussie reste autorisée.
  if (rulesState !== 'ready') {
    return (
      <div
        className="mx-auto max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-6 text-center"
        role={rulesState === 'error' ? 'alert' : 'status'}
      >
        <p className="text-sm text-amber-900">
          {rulesState === 'loading'
            ? 'Chargement des règles de votre événement…'
            : 'Impossible de charger les règles de votre événement. Vos réponses sont conservées.'}
        </p>
        {rulesState === 'error' && (
          <Button className="mt-3" onClick={() => setRulesAttempt((attempt) => attempt + 1)}>
            Réessayer
          </Button>
        )}
      </div>
    );
  }

  // 00312 (TICKET-DEROG-04) : rien à payer et dérogation manquante, bulletin
  // NON envoyé. Ni « bulletin enregistré », ni montant, ni réédition.
  if (phase === 'submitted' && outcome?.notSubmitted) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <h2 className="mb-2 text-xl font-semibold text-zinc-900">{BED_DEROGATION_HOLD_TITLE}</h2>
          <p className="text-sm text-amber-900" data-testid="bed-derogation-hold">
            {BED_DEROGATION_HOLD_MESSAGE}
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'submitted' && outcome) {
    // 00312 (TICKET-DEROG-04) : paiement du solde suspendu, dit par la
    // finalisation, le chargement ou le clic sur « Reprendre le paiement ».
    const paymentOnHold = outcome.derogationPending || Boolean(resumeState?.derogation_pending);
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <div
          className={`rounded-lg border p-6 text-center ${
            outcome.complete ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div className="mb-3 text-4xl">{outcome.complete ? '✓' : '⚠️'}</div>
          <h2 className="mb-2 text-xl font-semibold text-zinc-900">
            {outcome.complete
              ? 'Votre bulletin est complet et enregistré'
              : 'Votre bulletin est enregistré'}
          </h2>
          {!(paymentOnHold && outcome.complete) && (
            <p className="text-sm text-zinc-700">
              {outcome.complete
                ? outcome.nothingDue && !resumeState?.available
                  ? 'Votre solde est déjà réglé : il n’y a rien à payer.'
                  : outcome.degraded && soldeCoveredAtLoad
                    ? 'Votre solde est déjà couvert.'
                    : 'Il ne vous reste plus qu’à finaliser votre paiement.'
                : `Il reste ${outcome.missingCount} élément${
                    outcome.missingCount > 1 ? 's' : ''
                  } à nous transmettre. Vous pouvez revenir sur ce lien à tout moment.`}
            </p>
          )}
          {paymentOnHold && (
            <p
              className="mt-3 text-sm font-medium text-amber-800"
              data-testid="bed-derogation-pending"
            >
              {/* Rien à payer : c'est l'inscription définitive qui attend. */}
              {outcome.nothingDue
                ? BED_DEROGATION_FINAL_PENDING_MESSAGE
                : BED_DEROGATION_PENDING_MESSAGE}
            </p>
          )}
          {/* 00296 : rien à payer, bulletin encore incomplet : on le dit aussi. */}
          {outcome.nothingDue && !resumeState?.available && !outcome.complete && (
            <p className="mt-2 text-sm font-medium text-emerald-700">
              Votre solde est déjà réglé : il n’y a rien à payer.
            </p>
          )}
          {/* Recette du 07/10 (Floran) : le surplus se dit aussi après l'envoi. */}
          {outcome.nothingDue && !resumeState?.available && (amounts?.soldeCents ?? 0) < 0 && (
            <p className="mt-2 text-sm text-zinc-700" data-testid="bed-surplus">
              Votre acompte et votre réduction dépassent le total de{' '}
              {formatCents(-(amounts?.soldeCents ?? 0))}.
            </p>
          )}
          {/*
            Le montant n'est répété ICI que si rien d'autre ne le dit (revue
            adversariale 09/2026). Dès qu'un état de reprise existe, c'est LUI
            qui porte la vérité : le CTA annonce ce que le tunnel encaissera
            (reliquat de la promesse, 00240) et sa ligne d'aide le restant dû.
            Les deux côte à côte affichaient « Montant à régler : 2 000,00 € »
            trois lignes au-dessus de « Reste à régler : 1 920,00 € ».
          */}
          {outcome.amountToPayCents !== null &&
            !outcome.nothingDue &&
            !resumeState &&
            !paymentOnHold && (
              <p className="mt-3 text-sm font-medium text-zinc-900">
                Montant à régler :{' '}
                {formatCents(
                  promiseRemainderCents(
                    outcome.amountToPayCents,
                    amounts?.paiementsPartielsCents ?? 0
                  ) ?? Math.max(totalCents - (amounts?.paiementsPartielsCents ?? 0), 0)
                )}
              </p>
            )}

          {/*
            CTA de reprise — review Victor, P0 « cul-de-sac avant paiement ».
            Hors iframe, le `postMessage` de fin est ignoré : sans ce bouton,
            l'écran n'offrait AUCUNE sortie vers le paiement. Au rechargement
            d'un dossier soumis non réglé, c'est le seul chemin.

            Le jeton de tunnel n'est demandé qu'AU CLIC (`resumeBedPayment`) :
            le bootstrap n'en émet pas, il ne fait que dire qu'il reste à payer.
          */}
          {resumeState?.available && !paymentOnHold && (
            <div className="mt-5">
              <Button onClick={handleResumePayment} disabled={resumeBusy} className="w-full">
                {resumeBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Ouverture du paiement…
                  </>
                ) : (
                  // 00240 : on annonce le montant que le tunnel va RÉELLEMENT
                  // encaisser — le montant choisi s'il existe, pas le solde
                  // entier. Logique pure, testée dans wizard-model.
                  buildResumeCtaLabel(resumeState)
                )}
              </Button>
              {buildResumeCtaHint(resumeState) && (
                <p className="mt-2 text-xs text-zinc-600">{buildResumeCtaHint(resumeState)}</p>
              )}
              {resumeError && <p className="mt-2 text-sm text-red-600">{resumeError}</p>}
            </div>
          )}

          {/*
            Dossier déjà soldé (revue adverse F2) : un solde encaissé hors
            ligne avant la soumission, ou un clic sur « Reprendre » qui répond
            `already_settled`. On le DIT — avant, le bouton disparaissait sans
            explication.
          */}
          {resumeState?.settled && !resumeState.available && (
            <p className="mt-5 text-sm font-medium text-emerald-700">
              Votre solde est déjà réglé : il n'y a rien à payer.
            </p>
          )}

          {/*
            00305 (TICKET-BED-32) : bulletin envoyé sans paiement, puis un solde
            est de nouveau dû (réduction retirée, grille modifiée). Sans dossier
            de paiement, pas de reprise : le service administratif envoie le lien,
            qui fera ensuite apparaître « Reprendre le paiement ».
          */}
          {resumeState?.debt_reopened && !resumeState.available && (
            <p className="mt-5 text-sm font-medium text-amber-800" data-testid="bed-debt-reopened">
              Un solde de {formatCents(resumeState.remaining_cents ?? 0)} reste à régler. Notre
              service administratif va vous envoyer un lien de paiement.
            </p>
          )}
        </div>

        {outcome.degraded && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm">
            <p className="font-medium text-amber-800">
              {outcome.degraded === 'no_parent'
                ? 'Aucun acompte réglé n’a été retrouvé pour votre équipage : le solde ne peut pas être calculé automatiquement.'
                : soldeCoveredAtLoad
                  ? 'Votre solde est déjà couvert : notre service administratif doit finaliser votre dossier.'
                  : 'Le paiement du solde n’a pas pu être initié automatiquement.'}
            </p>
            <p className="mt-1 text-amber-700">
              Écrivez-nous à{' '}
              <a
                href="mailto:service.administratif@desertours.com"
                className="font-medium underline"
              >
                service.administratif@desertours.com
              </a>{' '}
              pour régulariser votre dossier.
            </p>
          </div>
        )}

        {/*
          00248 — réédition (retour recette du 04/09) : tant que l'équipage n'est
          ni « Inscrit » ni « BED complet », le participant rouvre son bulletin
          prérempli pour fournir ce qui manque. Décision serveur (bootstrap /
          statut après finalisation), jamais devinée ici.
        */}
        {resubmitAllowed && (
          <div className="rounded-lg border border-zinc-200 bg-white p-4 text-center">
            <p className="text-sm text-zinc-700">
              {outcome.complete
                ? 'Une information a changé ? Vous pouvez modifier votre bulletin.'
                : 'Vous avez récupéré une pièce ou une information manquante ? Complétez votre bulletin : vos réponses précédentes sont conservées.'}
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-3"
              onClick={() => void handleStartResubmit()}
              disabled={resubmitBusy}
              data-testid="bed-resubmit"
            >
              {resubmitBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
              {outcome.complete ? 'Modifier mon bulletin' : 'Compléter mon bulletin'}
            </Button>
          </div>
        )}

        {resubmitError && (
          <p
            className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
            role="alert"
          >
            {resubmitError}
          </p>
        )}

        <BedMissingBanner items={missingItems} submitted />
      </div>
    );
  }

  const step = BED_STEPS.find((s) => s.id === currentStep)!;
  const isLastStep = currentStep === BED_LAST_STEP;
  /**
   * Ce qui empêche d'avancer (ou d'envoyer) depuis l'étape affichée — la cause
   * NOMMÉE, y compris quand elle vit sur une autre étape. Cf. `describeStepBlocker`.
   */
  const blockerMessage = describeStepBlocker({
    step: currentStep,
    stepSatisfied: canLeaveStep(stepStates[currentStep], fieldsByStep[currentStep], ruleCtx),
    optionsBlocked: documentsPending,
    doubleRoomMissing,
    signaturesMissing: signaturesPending,
    certificatesMissing: certificatesPending,
  });

  return (
    <div className="space-y-6">
      {/* En-tête du BED : éléments manquants mis en avant à chaque retour. */}
      {isResubmitting && (
        <div
          className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900"
          role="status"
        >
          <p className="font-medium">
            Vous modifiez un bulletin déjà envoyé
            {bootstrap?.draft?.submitted_at
              ? ` le ${formatDate(bootstrap.draft.submitted_at)}`
              : ''}
            {bootstrap?.draft?.resubmitted_at
              ? `, mis à jour le ${formatDate(bootstrap.draft.resubmitted_at)}`
              : ''}
            .
          </p>
          <p className="mt-1">
            Vos réponses précédentes sont conservées. Renseignez ou corrigez ce qui doit l’être,
            puis validez à nouveau à la dernière étape.
          </p>
        </div>
      )}
      {isReturning && <BedMissingBanner items={missingItems} />}

      <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-5">
        <BedTimeline current={currentStep} states={stepStates} />
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
        <header className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              Étape {step.index} sur {BED_STEPS.length}
            </p>
            <h2 className="text-lg font-semibold text-zinc-900">{step.title}</h2>
            <p className="text-sm text-zinc-600">{step.hint}</p>
          </div>

          <span className="flex items-center gap-1.5 text-xs text-zinc-500" aria-live="polite">
            {saveState === 'saving' && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Enregistrement…
              </>
            )}
            {saveState === 'saved' && lastSavedAt && (
              <>
                <CloudUpload className="h-3.5 w-3.5 text-emerald-600" />
                Enregistré à{' '}
                {new Date(lastSavedAt).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </>
            )}
            {saveState === 'error' && (
              <span className="text-amber-700">Sauvegarde impossible — réessai automatique</span>
            )}
          </span>
        </header>

        {currentStep === 1 && crewNumber && (
          <p className="mb-5 rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
            Votre numéro d’équipage : <span className="font-semibold">{crewNumber}</span>
          </p>
        )}

        {currentStep === 5 && soldeCoveredAtLoad && (
          <p className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Votre solde est déjà couvert : il n’y a rien à payer, et aucune option payante ne peut
            être ajoutée en ligne. Pour une option, écrivez-nous à
            service.administratif@desertours.com.
          </p>
        )}

        {currentStep === 5 ? (
          <BedOptionsStep
            mandatoryItems={mandatoryItems}
            optionalItems={soldeCoveredAtLoad ? [] : optionalItems}
            optionsUnavailable={soldeCoveredAtLoad}
            selectedIds={selectedOptionIds}
            acknowledged={acknowledged}
            onToggleOption={handleToggleOption}
            onToggleAck={handleToggleAck}
            consentFields={fieldsByStep[5].filter((f) => isFieldVisible(f, ruleCtx))}
            values={values}
            isRequired={(field) => isFieldRequired(field, ruleCtx)}
            onChangeField={handleFieldChange}
            disabled={phase === 'submitting'}
            optionsLocked={optionsLocked}
            engagedOptionIds={engagedOptionIds}
            amounts={amounts}
            selectedOptions={selectedOptions}
            totalCents={totalCents}
            balanceDeadline={eventRules?.balance_deadline ?? null}
            resumeState={resumeState}
            doubleRoomAnswer={doubleRoomAnswer}
            onDoubleRoomChange={setDoubleRoomAnswer}
          />
        ) : currentStep === 6 ? (
          <BedRecapStep
            token={token}
            amounts={amounts}
            mandatoryItems={mandatoryItems}
            selectedOptions={selectedOptions}
            totalCents={totalCents}
            amountToPayCents={amountToPayCents}
            onAmountToPayChange={(cents) =>
              setAmountToPayCents(clampAmountToPay(cents, totalCents))
            }
            amountLocked={amountLocked}
            // 00240 : ce que le tunnel encaissera VRAIMENT (reliquat de la
            // promesse) et ce qui restera après. Sans lui, l'écran annonçait la
            // promesse brute — un chiffre que le tunnel ne prélèvera pas.
            resumeState={resumeState}
            roles={roles}
            roleLabels={roleLabels}
            roleNames={roleNames}
            signatures={signatures}
            onSignatureChange={handleSignature}
            certificates={certificates}
            onCertificateChange={handleCertificate}
            deferred={deferred}
            onToggleCertificateDeferral={(role, on) => handleToggleDeferral('certificat', role, on)}
            defaultDueDate={infoDeadline}
            balanceDeadline={eventRules?.balance_deadline ?? null}
            nothingDue={nothingDueAtLoad ? 'auto' : soldeCoveredAtLoad ? 'staff' : null}
            disabled={phase === 'submitting'}
            showErrors={showErrors}
          />
        ) : (
          renderStepFields(currentStep)
        )}

        {/*
          Message bloquant (revue adversariale 09/2026). Sur la dernière étape,
          `handleSubmit` refuse l'envoi quand une prise de connaissance manque —
          une cause qui vit à l'étape ⑤ et que ce bloc, conditionné à la seule
          étape courante, ne montrait pas : le bouton restait MUET. La décision
          est pure et testée (`describeStepBlocker`).
        */}
        {showErrors && blockerMessage && (
          <p
            className="mt-5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
            data-testid="bed-blocker"
          >
            {blockerMessage}
          </p>
        )}

        {submitError && (
          <p className="mt-5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {submitError}
          </p>
        )}

        {/* Navigation : empilée sur mobile, alignée sur desktop. */}
        <div className="mt-6 flex flex-col-reverse gap-2 border-t border-zinc-100 pt-5 sm:flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => void handlePrevious()}
            disabled={currentStep === 1 || phase === 'submitting'}
          >
            <ArrowLeft className="h-4 w-4" />
            Précédent
          </Button>

          {isLastStep ? (
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={phase === 'submitting'}
              data-testid="bed-submit"
            >
              {phase === 'submitting' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Envoi en cours…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {isResubmitting ? 'Mettre à jour mon bulletin' : 'Envoyer mon bulletin'}
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void handleNext()}
              disabled={phase === 'submitting'}
            >
              Continuer
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {!isEmbedded && (
        <p className="text-center text-xs text-zinc-500">
          Vos réponses sont enregistrées automatiquement : vous pouvez fermer cette page et revenir
          plus tard avec le même lien.
        </p>
      )}
    </div>
  );
}
